import type { ReactNode } from 'react';
import { type TableColumns } from '@ff-it/ui';
import { BoolIcon } from 'components/Icon';
import type { ProductKind } from 'types';
import { muted, name, nameButton } from './ProductList.css';

/** The common product shape needed by shared list cells. */
export type ProductListProduct = {
  id: number;
  name: string;
  kind: ProductKind;
  unit: string | null;
  gross_price: string | null;
  disabled: boolean;
  direction?: string | null;
  application?: string | null;
  outlet_or_service_category?: { name: string } | null;
};

export type ProductListRow<TProduct extends ProductListProduct = ProductListProduct> = {
  product: TProduct;
  listPrice: string | null;
};

export type ProductListColumnOptions<TProduct extends ProductListProduct> = {
  onOpen?: (product: TProduct) => void;
  showOutlet?: boolean;
  showListPrice?: boolean;
  kind?: ProductKind;
  /** Product-scoped columns rendered directly after the Product column. */
  afterProductColumns?: TableColumns<TProduct>;
};

const empty = (label: string): ReactNode => <span className={muted}>{label}</span>;

/**
 * Shared product-list columns. Contexts opt into outlet and list-price cells,
 * while the product name interaction is supplied by the owning surface.
 */
export function getProductListColumns<TProduct extends ProductListProduct>({
  onOpen,
  showOutlet = false,
  showListPrice = false,
  kind,
  afterProductColumns,
}: ProductListColumnOptions<TProduct> = {}): TableColumns<ProductListRow<TProduct>> {
  const columns: TableColumns<ProductListRow<TProduct>> = [
    {
      key: 'product',
      content: 'Product',
      width: '250px',
      render: ({ product }) => {
        const displayName = product.name || 'Generic product';
        const productName = <strong className={name}>{displayName}</strong>;
        if (!onOpen) return productName;
        return (
          <button
            type="button"
            className={nameButton}
            onClick={() => onOpen(product)}
            aria-label={`Edit ${displayName}`}
          >
            {productName}
          </button>
        );
      },
    },
  ];

  if (afterProductColumns?.length) {
    columns.push(...wrapProductColumns(afterProductColumns));
  }

  if (showOutlet) {
    columns.push({
      key: 'outlet',
      content: 'Outlet',
      render: ({ product }) => product.outlet_or_service_category?.name ?? empty('—'),
    });
  }

  if (kind !== 'SMART') {
    columns.push({
      key: 'unit',
      content: 'Units',
      width: '120px',
      render: ({ product }) => product.unit ?? empty('—'),
    });
  }

  if (showListPrice) {
    columns.push({
      key: 'listPrice',
      content: 'List price',
      width: '130px',
      render: ({ listPrice }) => listPrice ?? empty('—'),
    });
  }

  if (kind === 'SMART') {
    columns.push(
      {
        key: 'direction',
        content: 'Direction',
        width: '120px',
        render: ({ product }) => product.direction ?? empty('—'),
      },
      {
        key: 'application',
        content: 'Application',
        width: '120px',
        render: ({ product }) => product.application ?? empty('—'),
      },
    );
  } else {
    columns.push({
      key: 'fallbackPrice',
      content: 'Fallback price',
      width: '130px',
      render: ({ product }) => product.gross_price ?? empty('—'),
    });
  }

  columns.push({
    key: 'disabled',
    content: 'State',
    width: '64px',
    cellProps: { textAlign: 'center' },
    render: ({ product }) => <BoolIcon>{!product.disabled}</BoolIcon>,
  });

  return columns;
}

/** Adapt type-owned or context-owned product columns to shared list rows. */
function wrapProductColumns<TProduct extends ProductListProduct>(
  columns: TableColumns<TProduct>,
): TableColumns<ProductListRow<TProduct>> {
  return columns.map((column) => {
    const { render } = column;
    return {
      ...column,
      render: render ? (row, rowIndex, cellIndex) => render(row.product, rowIndex, cellIndex) : undefined,
    };
  });
}
